import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Input, Modal, Radio, Segmented, Spin } from 'antd';
import styled from '@emotion/styled';
import { colorConstants } from 'config/colorConstants';
import { EditModalType } from '../ScenarioTableWrapper';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { EditableFieldsType } from 'types/pricingSimulator';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import { UserContext } from 'contexts/ConfigContext';
import { KpiAttributeConfig, fetchCurrency, getKpiAttributeConfig, sendNotification } from 'utils/helperMethods';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

export const inputTileData = [
    { value: 'incremental', label: 'Increment Abs. Change' },
    { value: 'percentage', label: '% Change' },
    { value: 'absolute', label: 'Set Final Value' },
];

const CustomSegmented = styled(Segmented)`
    .ant-segmented-item-selected {
        border-radius: 4px;
    }
    .ant-segmented-item {
        flex: 1;
    }
    .ant-segmented-group {
        justify-content: space-between;
    }
`;

export const CustomModal = styled(Modal)`
    .ant-modal-content {
        border-radius: 8px !important;
    }
    .ant-modal-header {
        border-radius: 8px 8px 0px 0px !important;
        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 500 !important;
        padding-right: 24px;
    }
    .ant-modal-body {
        padding: 16px !important;
        padding-top: 0px !important;
    }
    .ant-btn-primary.ant-btn-dangerous:not([disabled]) {
        background-color: #ff4d4f !important;
        border-color: #ff4d4f !important;
        color: #fff !important;
    }
`;

type Props = {
    data: EditModalType;
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitClick: (data: any) => void;
    loading: boolean;
};

const ScenarioColumnEditModal: FC<Props> = ({ data, onCancel, open, onSubmitClick, loading }) => {
    const [inputTile, setInputTile] = useState(inputTileData[0]);
    const [inputTileDataOptions, setInputTileDataOptions] = useState(inputTileData);
    const [applyValue, setApplyValue] = useState<EditableFieldsType>(data?.from === 'cell' ? 'cell' : 'all');
    const [value, setValue] = useState('');
    const [kpiConfig, setKpiConfig] = useState<KpiAttributeConfig | null>(null);
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);

    const { dimensionCheckboxObj, selectedScenarioInfo, isDropdownAtLowestLevel } =
        useContext(pricingSimulatorDataContext);
    const { appConfig } = useContext(UserContext);
    const { data: kpiData } = data;
    const kpiId = data?.data?.kpiId;

    const currency = fetchCurrency(appConfig);

    useEffect(() => {
        if (selectedScenarioInfo?.lenseConfig?.length && kpiId) {
            const config = getKpiAttributeConfig(kpiId, selectedScenarioInfo.lenseConfig);
            if (config) {
                setKpiConfig(config);
                const newInputTileList = [];
                if (config.allowIncrementChange) newInputTileList.push(inputTileData[0]);
                if (config.allowAbsChange) newInputTileList.push(inputTileData[1]);
                // at higher level remove set final value option
                if (config.allowFinalValueChange && isDropdownAtLowestLevel) {
                    newInputTileList.push(inputTileData[2]);
                }
                if (newInputTileList?.length > 0) setInputTile(newInputTileList[0]);
                setInputTileDataOptions(newInputTileList);
            } else {
                setKpiConfig(null);
            }
        }
    }, [kpiId, selectedScenarioInfo?.lenseConfig]);

    useEffect(() => {
        //set radio button
        if (open) {
            setApplyValue(data?.from === 'cell' ? 'cell' : 'all');
        }
        //reset popup content to default on popup close
        return () => {
            setInputTile(inputTileData[0]);
            setApplyValue(data?.from === 'cell' ? 'cell' : 'all');
            setValue('');
        };
    }, [open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newValue = Number(value);

        if (inputTile.value === 'absolute' && !kpiConfig?.allowNegative && newValue < 0) {
            sendNotification('error', 'Cannot enter negative number');
            return;
        }

        // Check for decimals if not allowed
        if (kpiConfig?.allowedDecimals === 0 && value.includes('.')) {
            sendNotification('error', 'Cannot have decimals');
            return;
        }

        setValue(value);
    };

    const errorMessage = useMemo(() => {
        const message = `Please include ${dimensionConfigData?.defaultDim1DropdownItem?.label} and ${dimensionConfigData?.defaultDim2DropdownItem?.label} to enable edit`;
        return message;
    }, [inputTileDataOptions]);

    return (
        <CustomModal
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{ disabled: !value || isNaN(Number(value)), style: { borderRadius: 6 } }}
            onOk={() => {
                if (loading) return;
                onSubmitClick({
                    kpiData,
                    value,
                    inputTile,
                    applyValue,
                });
            }}
            okText={loading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : 'Apply'}
            destroyOnClose
            centered
            open={open}
            width={420}
            onCancel={(e) => {
                if (loading) return;
                onCancel(e);
            }}
            title={`Edit: ${kpiData?.kpiName}`}
        >
            <div className="flex-col flex gap-1">
                {inputTileDataOptions?.length === 0 ? (
                    <div className="pl-3 font-xs text-backgroundGreyAlt7"> {errorMessage}</div>
                ) : (
                    <>
                        {/* Tabs section */}
                        <CustomSegmented
                            //block
                            style={{ fontSize: 12, borderRadius: 4 }}
                            onChange={(val) => {
                                const selectedItem = inputTileData?.find((item) => {
                                    return item?.value === val;
                                });
                                if (selectedItem) setInputTile(selectedItem);
                            }}
                            value={inputTile.value}
                            options={inputTileDataOptions}
                        />
                        <div className="flex flex-col px-2 py-4 gap-4 border border-backgroundGreyAlt6 rounded">
                            {/* INPUT section */}
                            <div className="flex items-center">
                                <p className="text font-xs mr-2 w-[180px] text-right text-blackAlt flex items-center  justify-end">
                                    <span className="mr-1 text-blackAlt">{inputTile.label}</span>{' '}
                                    {/* <QuestionCircleOutlined style={{ color: 'gray', marginRight: 2 }} /> */}:
                                </p>
                                <div className="relative flex gap-2 justify-center items-center">
                                    <Input
                                        type="number"
                                        value={value}
                                        style={{ fontSize: 13, width: 128, borderRadius: 6 }}
                                        placeholder="Input"
                                        onChange={handleChange}
                                    />
                                    <span className="font-xs text-fontGreyAlt ">
                                        {inputTile.value === inputTileData[1].value ? '%' : currency}
                                    </span>
                                </div>
                            </div>

                            {/* RADIO BUTTONS section */}
                            <Radio.Group
                                onChange={(e) => setApplyValue(e.target.value)}
                                value={applyValue}
                                style={{ display: 'flex', flexDirection: 'column', marginLeft: '10px' }}
                            >
                                <Radio
                                    disabled={data?.from !== 'cell'}
                                    style={{ fontSize: 12, color: colorConstants.blackAlt1 }}
                                    value={'cell'}
                                >
                                    Apply to the Cell Only
                                </Radio>

                                <Radio
                                    disabled={Object.keys(dimensionCheckboxObj || {}).length <= 0}
                                    style={{ fontSize: 12, color: colorConstants.blackAlt1 }}
                                    value={'selected'}
                                >
                                    Apply to Selected Only
                                </Radio>
                                <Radio
                                    style={{ fontSize: 12, color: colorConstants.blackAlt1 }}
                                    value={'all'}
                                >{`Apply to All in ${
                                    kpiData?.kpiName
                                } ( ${selectedScenarioInfo?.count?.toLocaleString()} items )`}</Radio>
                            </Radio.Group>
                        </div>
                    </>
                )}
            </div>
        </CustomModal>
    );
};

export default ScenarioColumnEditModal;
